import React from 'react';
import { Gift } from 'lucide-react';

/**
 * Progress bar with reward tier markers.
 * Used in Victories page (md) and LevelOverview card (sm).
 *
 * @param {number} count - Badges unlocked
 * @param {number} total - Total badges available
 * @param {Array}  rewardTiers - [{badgeThreshold, percentage, earned?}]
 * @param {string} color - Level hex color
 * @param {'sm'|'md'} size - sm = compact (overview card), md = full (victories)
 * @param {'card'|'banner'} variant - card = standard, banner = white-on-colored bg
 */
const RewardProgressBar = ({ count = 0, total = 10, rewardTiers = [], color, size = 'md', variant = 'card' }) => {
    const pct = total > 0 ? Math.min((count / total) * 100, 100) : 0;
    const isSm = size === 'sm';
    const isBanner = variant === 'banner';

    const trackBg = isBanner ? 'bg-white/15' : 'bg-gray-200 dark:bg-white/10';
    const barH = isSm ? 'h-[3px]' : 'h-1.5';

    return (
        <div className={`relative w-full ${isSm ? 'py-2' : 'py-4'}`}>
            {/* Track */}
            <div className={`relative w-full ${barH} ${trackBg} rounded-full`}>
                {/* Fill */}
                <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out`}
                    style={{
                        width: `${pct}%`,
                        backgroundColor: isBanner ? 'rgba(255,255,255,0.5)' : color,
                    }}
                />

                {/* Tier markers */}
                {rewardTiers.map((tier, i) => {
                    if (!tier.badgeThreshold || tier.badgeThreshold <= 0) return null;
                    const pos = total > 0 ? (tier.badgeThreshold / total) * 100 : 0;
                    const isEarned = tier.earned ?? (count >= tier.badgeThreshold);

                    const iconColor = isBanner
                        ? (isEarned ? 'text-amber-300' : 'text-white/30')
                        : (isEarned ? 'text-amber-500' : 'text-gray-400 dark:text-gray-600');
                    const labelColor = isBanner
                        ? (isEarned ? 'text-amber-300' : 'text-white/40')
                        : (isEarned ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500');
                    const notchBg = isBanner
                        ? (isEarned ? 'bg-amber-300' : 'bg-white/20')
                        : (isEarned ? 'bg-amber-500' : 'bg-gray-300 dark:bg-white/20');

                    return (
                        <div
                            key={i}
                            className="absolute flex flex-col items-center pointer-events-none"
                            style={{ left: `${pos}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
                        >
                            {/* Icon above */}
                            <Gift className={`${isSm ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} ${iconColor} ${isSm ? '-mt-2' : '-mt-3'} mb-0.5`} />
                            {/* Notch line */}
                            <div className={`w-px ${isSm ? 'h-1.5' : 'h-2.5'} ${notchBg} rounded-full`} />
                            {/* Percentage label (md only) */}
                            {!isSm && (
                                <span className={`text-[9px] font-bold mt-0.5 whitespace-nowrap ${labelColor}`}>
                                    -{tier.percentage}%
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RewardProgressBar;
